import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Table, DatePicker, Space, Button } from 'antd';
import { PieChart, Pie, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import AV from 'leancloud-storage';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;

const ExamStats = () => {
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState([dayjs().startOf('month'), dayjs().endOf('month')]);
  const [stats, setStats] = useState({
    totalExams: 0,
    totalStudentAttempts: 0,
    examTypeStats: {
      '科目一': { total: 0, passed: 0, rate: 0 },
      '科目二': { total: 0, passed: 0, rate: 0 },
      '科目三': { total: 0, passed: 0, rate: 0 },
      '科目四': { total: 0, passed: 0, rate: 0 }
    },
    monthlyExams: [],
    ageDistribution: [],
    examinerStats: []
  });

  const handleQuickSelect = (period) => {
    switch (period) {
      case 'today':
        setDateRange([dayjs().startOf('day'), dayjs().endOf('day')]);
        break;
      case 'week':
        setDateRange([dayjs().startOf('week'), dayjs().endOf('week')]);
        break;
      case 'month':
        setDateRange([dayjs().startOf('month'), dayjs().endOf('month')]);
        break;
      case 'year':
        setDateRange([dayjs().startOf('year'), dayjs().endOf('year')]);
        break;
    }
  };

  const fetchStats = async () => {
    try {
      setLoading(true);
      const [startDate, endDate] = dateRange;

      const query = new AV.Query('ExamBooking');
      query.greaterThanOrEqualTo('date', startDate.toDate());
      query.lessThanOrEqualTo('date', endDate.toDate());
      query.limit(1000);
      const exams = await query.find();

      // Basic stats
      const totalExams = exams.length;
      let totalStudentAttempts = 0;
      const examTypeStats = {
        '科目一': { total: 0, passed: 0, rate: 0 },
        '科目二': { total: 0, passed: 0, rate: 0 },
        '科目三': { total: 0, passed: 0, rate: 0 },
        '科目四': { total: 0, passed: 0, rate: 0 }
      };

      // Monthly distribution
      const monthlyData = {};
      
      // Examiner stats
      const examinerData = {};
      
      // Process each exam
      console.log('=== Processing Exams ===');
      for (const exam of exams) {
        const examType = exam.get('examType');
        const students = exam.get('students') || [];
        const passedCount = exam.get('passedCount') || 0;
        const examiner = exam.get('examiner') || '未知考官';
        const examDate = dayjs(exam.get('date').iso || exam.get('date'));
        const monthKey = examDate.format('YYYY-MM');

        console.log(`Exam Type: ${examType}`);
        console.log(`Students in exam: ${students.length}`);
        console.log(`Passed Count: ${passedCount}`);

        totalStudentAttempts += students.length;
        examTypeStats[examType].total += students.length;
        examTypeStats[examType].passed += passedCount;

        // Update monthly stats
        monthlyData[monthKey] = monthlyData[monthKey] || {
          month: monthKey,
          total: 0,
          passed: 0
        };
        monthlyData[monthKey].total += students.length;
        monthlyData[monthKey].passed += passedCount;

        // Update examiner stats
        examinerData[examiner] = examinerData[examiner] || {
          name: examiner,
          total: 0,
          passed: 0
        };
        examinerData[examiner].total += students.length;
        examinerData[examiner].passed += passedCount;
      }

      // Calculate pass rates
      console.log('=== Final Stats ===');
      console.log('Total Student Attempts:', totalStudentAttempts);
      console.log('Exam Type Stats:', JSON.stringify(examTypeStats, null, 2));

      Object.keys(examTypeStats).forEach(type => {
        examTypeStats[type].rate = examTypeStats[type].total > 0
          ? (examTypeStats[type].passed / examTypeStats[type].total * 100).toFixed(2)
          : 0;
      });

      // Convert to arrays and sort
      const monthlyExams = Object.values(monthlyData)
        .sort((a, b) => a.month.localeCompare(b.month))
        .map(item => ({
          ...item,
          passRate: item.total > 0 
            ? (item.passed / item.total * 100).toFixed(2)
            : 0
        }));

      const examinerStats = Object.values(examinerData)
        .map(item => ({
          ...item,
          passRate: item.total > 0
            ? (item.passed / item.total * 100).toFixed(2)
            : 0
        }))
        .sort((a, b) => b.total - a.total);

      setStats({
        totalExams,
        totalStudentAttempts,
        examTypeStats,
        monthlyExams,
        examinerStats
      });

    } catch (error) {
      console.error('Error fetching exam stats:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [dateRange]);

  const renderOverview = () => {
    // Log the calculation of average pass rate
    const totalStudentAttempts = stats.totalStudentAttempts;
    const totalPassed = Object.values(stats.examTypeStats).reduce((sum, { passed }) => sum + passed, 0);
    
    console.log('=== Pass Rate Calculation ===');
    console.log('Total Attempts:', totalStudentAttempts);
    console.log('Total Passed:', totalPassed);
    console.log('Pass Rate:', (totalPassed / totalStudentAttempts * 100).toFixed(2) + '%');

    return (
      <Card title="考试概览">
        <Row gutter={16}>
          <Col span={8}>
            <Statistic
              title="总考试场次"
              value={stats.totalExams}
              loading={loading}
            />
          </Col>
          <Col span={8}>
            <Statistic
              title="总考试人次"
              value={stats.totalStudentAttempts}
              loading={loading}
            />
          </Col>
          <Col span={8}>
            <Statistic
              title="平均通过率"
              value={
                stats.totalStudentAttempts > 0
                  ? (totalPassed / totalStudentAttempts * 100).toFixed(2)
                  : 0
              }
              suffix="%"
              loading={loading}
            />
          </Col>
        </Row>
      </Card>
    );
  };

  const renderPassRates = () => (
    <Card title="各科目通过率" style={{ marginTop: 16 }}>
      <Table
        dataSource={Object.entries(stats.examTypeStats).map(([type, data]) => ({
          key: type,
          type,
          ...data
        }))}
        columns={[
          {
            title: '科目',
            dataIndex: 'type',
            key: 'type'
          },
          {
            title: '总人数',
            dataIndex: 'total',
            key: 'total'
          },
          {
            title: '通过人数',
            dataIndex: 'passed',
            key: 'passed'
          },
          {
            title: '通过率',
            dataIndex: 'rate',
            key: 'rate',
            render: (text) => `${text}%`
          }
        ]}
        pagination={false}
      />
    </Card>
  );

  const renderMonthlyTrend = () => (
    <Card title="月度考试趋势" style={{ marginTop: 16 }}>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={stats.monthlyExams}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="month" />
          <YAxis />
          <Tooltip />
          <Legend />
          <Bar name="考试人数" dataKey="total" fill="#8884d8" />
          <Bar name="通过人数" dataKey="passed" fill="#82ca9d" />
        </BarChart>
      </ResponsiveContainer>
    </Card>
  );

  const renderExaminerStats = () => (
    <Card title="考官统计" style={{ marginTop: 16 }}>
      <Table
        dataSource={stats.examinerStats}
        columns={[
          {
            title: '考官',
            dataIndex: 'name',
            key: 'name'
          },
          {
            title: '考试总人数',
            dataIndex: 'total',
            key: 'total'
          },
          {
            title: '通过人数',
            dataIndex: 'passed',
            key: 'passed'
          },
          {
            title: '通过率',
            dataIndex: 'passRate',
            key: 'passRate',
            render: (text) => `${text}%`,
            sorter: (a, b) => a.passRate - b.passRate
          }
        ]}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showQuickJumper: true
        }}
      />
    </Card>
  );

  return (
    <div>
      <Card style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
          <Space>
            <Button onClick={() => handleQuickSelect('today')}>今天</Button>
            <Button onClick={() => handleQuickSelect('week')}>本周</Button>
            <Button onClick={() => handleQuickSelect('month')}>本月</Button>
            <Button onClick={() => handleQuickSelect('year')}>今年</Button>
          </Space>
          <RangePicker
            value={dateRange}
            onChange={setDateRange}
            style={{ width: 300 }}
          />
        </Space>
      </Card>
      {renderOverview()}
      {renderPassRates()}
      {renderMonthlyTrend()}
      {renderExaminerStats()}
    </div>
  );
};

export default ExamStats; 